import { useFormation } from '@contexts/FormationContext/FormationContext';
import { useGetFormationQuestionsMutation, FormationQuestion, useGetFormationsMutation } from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type Answerable<T> = T & {
    answer: number | null;
    answerToDisplay?: number;
};

export const useFormationTalkQuestionsPageVM = () => {
    const [questions, setQuestions] = useState<Answerable<FormationQuestion>[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [pageTitle, setPageTitle] = useState<string>('');
    const [showResults, setShowResults] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [currentScore, setCurrentScore] = useState<number>(0);
    const [explanationsToDisplay, setExplanationsToDisplay] = useState<number[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);

    const { categoryId, formationId } = useParams();
    const navigate = useNavigate();
    const { setQuestionsAnswers, setScore } = useFormation();

    const [getFormationQuestions, { isLoading, data: questionsData, error }] = useGetFormationQuestionsMutation();
    const [getFormations, { data: formations }] = useGetFormationsMutation();

    const canGoBack = useMemo(() => {
        return currentQuestionIndex > 0 && !showResults;
    }, [currentQuestionIndex, showResults]);

    const canGoNext = useMemo(() => {
        if (showResults) return false;
        if (!questions.length) return false;

        const currentQuestion = questions[currentQuestionIndex];
        return currentQuestion && currentQuestion.answer !== null;
    }, [currentQuestionIndex, questions, showResults]);

    const isLastQuestion = useMemo(() => {
        return currentQuestionIndex === questions.length - 1;
    }, [currentQuestionIndex, questions.length]);

    const handleNext = () => {
        if (isLastQuestion) {
            setShowResults(true);
            return;
        }
        setCurrentQuestionIndex(currentQuestionIndex + 1);
    };

    const handlePrev = () => {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
    };

    const selectAnswer = (questionId: number, answerId: number) => {
        const question = questions.find((question) => question.id === questionId);
        if (!question) {
            return;
        }

        const hasAlreadyAnswered = question.answer !== null;
        if (hasAlreadyAnswered) {
            return;
        }

        setExplanationsToDisplay((prev) => [...prev, questionId]);

        const rightAnswer = question.formation_answers.find((answer) => answer.is_correct);

        setQuestions(
            questions.map((question) => {
                if (question.id === questionId) {
                    return { ...question, answer: answerId, answerToDisplay: rightAnswer?.id };
                }
                return question;
            })
        );

        setTimeout(() => {
            const scrollContainer = document.querySelector('.rendered-page') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
            }
        }, 100);
    };

    const isEveryQuestionAnswered = useMemo(() => {
        if (!questions.length) {
            return false;
        }

        return questions.every((question) => question.answer !== null);
    }, [questions]);

    useEffect(() => {
        if (!formationId) return;
        getFormationQuestions({ formationId: parseInt(formationId) });
        getFormations({ categoryId: parseInt(categoryId ?? '0') });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formationId]);

    useEffect(() => {
        if (questionsData) {
            setQuestions(questionsData.map((question) => ({ ...question, answer: null })));
        }

        if (error) {
            setErrorMessage('Une erreur est survenue lors du chargement des questions');
        }
    }, [questionsData, error]);

    useEffect(() => {
        if (!formations || !formationId) return;

        const currentFormation = formations.find((formation) => formation.id === parseInt(formationId));

        if (!currentFormation) {
            setErrorMessage('Formation non trouvée');
            return;
        }

        setPageTitle(currentFormation.title);
    }, [formations, formationId]);

    useEffect(() => {
        if (!showResults || !isEveryQuestionAnswered || !formationId) {
            return;
        }

        const scoreValue = questions.filter((question) => question.answer === question.answerToDisplay).length;
        setCurrentScore(scoreValue);

        if (!validated) {
            return;
        }

        setQuestionsAnswers(
            questions.map((question) => ({
                questionId: question.id,
                answerId: question.answer ?? 0
            }))
        );
        setScore(scoreValue);
    }, [showResults, questions, formationId, setQuestionsAnswers, setScore, isEveryQuestionAnswered, validated]);

    return {
        pageTitle,
        questions,
        isLoading,
        selectAnswer,
        explanationsToDisplay,
        isEveryQuestionAnswered,
        showResults,
        currentScore,
        navigate,
        setValidated,
        currentQuestionIndex,
        canGoBack,
        canGoNext,
        isLastQuestion,
        handleNext,
        handlePrev,
        currentQuestion: questions[currentQuestionIndex],
        errorMessage,
        validated,
        categoryId,
        formationId
    };
};
